import {
  getDatabase,
  onValue,
  ref,
  onDisconnect as rtdbOnDisconnect,
  serverTimestamp as rtdbServerTimestamp,
  set,
  Database,
} from 'firebase/database';
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';

import { app, db } from './config';

// Lazy-load Realtime Database - only initialize on client-side
let rtdb: Database | null = null;

const getRtdb = (): Database | null => {
  // Only initialize on client-side to avoid build errors
  if (typeof window === 'undefined') {
    return null;
  }
  
  if (!rtdb && app) {
    try {
      // Get database URL from environment or use default format
      const databaseURL = process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL || 
        `https://${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}-default-rtdb.firebaseio.com`;
      rtdb = getDatabase(app, databaseURL);
    } catch (error) {
      // If database URL is invalid or not configured, return null
      return null;
    }
  }
  
  return rtdb;
};

export const onlineStatusService = {
  setupOnlineStatus: async (userId: string) => {
    if (!userId) return;

    const rtdbInstance = getRtdb();
    if (!rtdbInstance) return;

    try {
      const userStatusRef = ref(rtdbInstance, `status/${userId}`);

      const userDocRef = doc(db, 'users', userId);

      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        return;
      }

      const isOnlineData = {
        state: 'online',
        lastChanged: rtdbServerTimestamp(),
      };

      const isOfflineData = {
        state: 'offline',
        lastChanged: rtdbServerTimestamp(),
      };

      rtdbOnDisconnect(userStatusRef).set(isOfflineData).catch(() => {
        // Ignore onDisconnect errors
      });

      try {
        await set(userStatusRef, isOnlineData);
      } catch (error: any) {
        // Handle permission denied or other Firebase errors silently
        // This is expected if Firebase Realtime Database rules don't allow write
        if (error?.code !== 'PERMISSION_DENIED') {
          // Only log non-permission errors for debugging
          if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
            // Only log in development
          }
        }
        return; // Exit early if we can't set status
      }

      try {
        await updateDoc(userDocRef, {
          isOnline: true,
          lastSeen: serverTimestamp(),
        });
      } catch (error: any) {
        // Handle Firestore errors silently
        if (error?.code !== 'PERMISSION_DENIED') {
          // Only log non-permission errors
        }
      }

      onValue(userStatusRef, async (snapshot) => {
        const status = snapshot.val();

        if (status?.state === 'offline') {
          try {
            await updateDoc(userDocRef, {
              isOnline: false,
              lastSeen: serverTimestamp(),
            });
          } catch (error: any) {
            // Handle Firestore errors silently
            if (error?.code !== 'PERMISSION_DENIED') {
              // Only log non-permission errors
            }
          }
        }
      }, (error) => {
        // Handle read permission errors
        if (error?.code !== 'PERMISSION_DENIED') {
          // Only log non-permission errors
        }
      });
    } catch (error) {
      // Error setting up online status
    }
  },

  getOnlineStatus: (userId: string, callback: (isOnline: boolean) => void) => {
    if (!userId) return () => {};

    const rtdbInstance = getRtdb();
    if (!rtdbInstance) return () => {};

    try {
      const userStatusRef = ref(rtdbInstance, `status/${userId}`);

      const unsubscribe = onValue(
        userStatusRef,
        (snapshot) => {
          const status = snapshot.val();

          callback(status?.state === 'online');
        },
        (error) => {
          // Handle permission denied errors silently
          if (error?.code !== 'PERMISSION_DENIED') {
            // Only log non-permission errors
          }
          // Return offline status as fallback
          callback(false);
        }
      );

      return unsubscribe;
    } catch (error) {
      return () => {};
    }
  },

  setOfflineStatus: async (userId: string) => {
    if (!userId) return;

    const rtdbInstance = getRtdb();
    if (!rtdbInstance) return;

    try {
      const userStatusRef = ref(rtdbInstance, `status/${userId}`);

      const userDocRef = doc(db, 'users', userId);

      try {
        await set(userStatusRef, {
          state: 'offline',
          lastChanged: rtdbServerTimestamp(),
        });
      } catch (error: any) {
        // Handle permission denied errors silently
        if (error?.code !== 'PERMISSION_DENIED') {
          // Only log non-permission errors
        }
      }

      try {
        await updateDoc(userDocRef, {
          isOnline: false,
          lastSeen: serverTimestamp(),
        });
      } catch (error: any) {
        // Handle Firestore errors silently
        if (error?.code !== 'PERMISSION_DENIED') {
          // Only log non-permission errors
        }
      }
    } catch (error) {
      // Error setting offline status
    }
  },
};
